import React from 'react';
import { Helmet } from 'react-helmet';
import './About.css';
import { useLanguage } from '../../i18n/LanguageContext';
import { translations } from '../../i18n/translations';
import config from '../../config';

/**
 * Proje ekibi isimleri — tek kaynak (TR/EN). FR görünümü: ABOUT_TEAM_FR + translations.fr.about.team başlıkları.
 */
const ABOUT_TEAM = {
  projectLead: ['Doç. Dr. Zeki ERASLAN'],
  executiveCommittee: [
    'Doç. Dr. Zeki ERASLAN',
    'Dr. Ömer GÖK',
    'Evre ÇORUH',
    'Prof. Dr. Suna TİMUR AĞILDERE',
    'Prof. Dr. Mustafa AKSOY',
  ],
  scienceBoard: [
    'Prof. Dr. Suna TİMUR AĞILDERE',
    'Prof. Dr. Mustafa AKSOY',
    'Prof. Dr. Mustafa SANDIKÇI',
    'Prof. Dr. Evren GÜÇER',
  ],
  languageEditors: [
    { roleKey: 'turkishEditorRole', name: 'Zeynep AKKİRAZ AYHAN' },
    { roleKey: 'frenchEditorRole', name: 'Prof. Dr. Suna TİMUR AĞILDERE' },
    { roleKey: 'englishEditorRole', name: 'Prof. Dr. YALÇIN ARSLANTÜRK' },
  ],
  translators: [
    { roleKey: 'frenchAssistantRole', name: 'Dr. Öğr. Üyesi Nesibe ERKALAN' },
    { roleKey: 'englishAssistantRole', name: 'Dr. Öğr. Üyesi Engin AYTEKİN' },
    { roleKey: 'englishAssistantRole', name: 'Öğretim Görevlisi Barış Ak' },
  ],
  coordinationTeam: ['Zeynep AKKİRAZ AYHAN', 'Adile ÖZGÜNAY', 'Irmak Güneş YÜCEİL YILDIRIM'],
};

/** Titres et formulations FR (noms / formulations locales). */
const ABOUT_TEAM_FR = {
  projectLead: ['Mt de Conf. Dr. Zeki ERASLAN'],
  executiveCommittee: [
    'Mt de Conf. Dr. Zeki ERASLAN',
    'Dr. Ömer GÖK',
    'Evre ÇORUH',
    'Prof. Dr. Suna TİMUR AĞILDERE',
    'Prof. Dr. Mustafa AKSOY',
  ],
  scienceBoard: ABOUT_TEAM.scienceBoard,
  languageEditors: [
    { roleKey: 'turkishEditorRole', name: 'Zeynep AKKİRAZ AYHAN' },
    { roleKey: 'frenchEditorRole', name: 'Prof. Dr. Suna TİMUR AĞILDERE' },
    { roleKey: 'englishEditorRole', name: 'Prof. Dr. YALÇIN ARSLANTÜRK' },
  ],
  translators: [
    { roleKey: 'frenchAssistantRole', name: 'Dr. Nesibe ERKALAN' },
    { roleKey: 'englishAssistantRole', name: 'Dr. Engin AYTEKİN' },
    { roleKey: 'englishAssistantRole', name: 'Lector Barış Ak' },
  ],
  coordinationTeam: ABOUT_TEAM.coordinationTeam,
};

function selectAboutBundle(locale) {
  const pack = translations[locale]?.about ?? translations.tr.about;
  return pack;
}

function scopeParagraphs(scope) {
  const ordered = [];
  for (let i = 1; i <= 12; i += 1) {
    const raw = scope[`p${i}`];
    if (raw != null && String(raw).trim() !== '') ordered.push(raw);
  }
  return ordered.flatMap((text, pi) =>
    String(text)
      .split(/\n\n+/)
      .filter(Boolean)
      .map((chunk, ci) => ({ key: `scope-${pi}-${ci}`, text: chunk }))
  );
}

const About = () => {
  const { locale } = useLanguage();
  const about = selectAboutBundle(locale);
  const team = about.team;
  const teamRows = locale === 'fr' ? ABOUT_TEAM_FR : ABOUT_TEAM;
  const introParas = [about.intro.p1, about.intro.p2, about.intro.p3, about.intro.p4].filter(Boolean);
  const mainTitle = typeof about.title === 'string' ? about.title.trim() : '';
  const navAbout = translations[locale]?.nav?.about ?? translations.tr.nav.about;
  const documentTitle = mainTitle
    ? `${mainTitle} — ${config.project.name}`
    : `${navAbout} — ${config.project.name}`;

  return (
    <div className="about-container">
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>
      <div className="about-content">
        {mainTitle ? <h1 className="about-title">{mainTitle}</h1> : null}

        <section
          className="about-section"
          aria-labelledby={about.introSectionTitle ? 'about-intro-section-title' : undefined}
        >
          {about.introSectionTitle ? (
            <h2 id="about-intro-section-title">{about.introSectionTitle}</h2>
          ) : null}
          {introParas.map((text, i) => (
            <p key={`intro-${i}`}>{text}</p>
          ))}
        </section>

        <section className="about-section">
          <h2 id="about-scope-title">{about.scope.title}</h2>
          {scopeParagraphs(about.scope).map(({ key, text }) => (
            <p key={key}>{text}</p>
          ))}
        </section>

        <section className="about-section" aria-labelledby="about-team-title">
          <h2 id="about-team-title" className="project-team-title">
            {team.title}
          </h2>
          <img
            src="/images/cdtst/dekoratif.png"
            alt={team.decorativeAlt}
            className="decorative-line"
          />

          <div className="team-grid">
            <div className="team-column">
              <h3>{team.projectLead}</h3>
              {teamRows.projectLead.map((name) => (
                <p key={name}>{name}</p>
              ))}
            </div>

            <div className="team-column">
              <h3>{team.executiveCommittee}</h3>
              {teamRows.executiveCommittee.map((name) => (
                <p key={name}>{name}</p>
              ))}
            </div>

            <div className="team-column">
              <h3>{team.scienceBoard}</h3>
              {teamRows.scienceBoard.map((name) => (
                <p key={name}>{name}</p>
              ))}
            </div>

            <div className="team-column">
              <h3>{team.languageEditors}</h3>
              {teamRows.languageEditors.map(({ roleKey, name }) => (
                <p key={`${roleKey}-${name}`} className="team-role-line">
                  <span className="team-role">{team[roleKey]}</span>
                  <span className="team-name">{name}</span>
                </p>
              ))}
            </div>

            <div className="team-column">
              <h3>{team.translators}</h3>
              {teamRows.translators.map(({ roleKey, name }) => (
                <p key={`${roleKey}-${name}`} className="team-role-line">
                  <span className="team-role">{team[roleKey]}</span>
                  <span className="team-name">{name}</span>
                </p>
              ))}
            </div>

            <div className="team-column">
              <h3>{team.coordinationTeam}</h3>
              {teamRows.coordinationTeam.map((name) => (
                <p key={name}>{name}</p>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
